<template>
  <div class="camp-mine-shell">
    <!-- 背景上的个人信息、角色切换 -->
    <div class="mine-head">
      <div class="mine-profile" v-if="!fromPd">
        <img class="profile-avatar" src="@/assets/images/researchCamp/avatar.png" alt="">
        <div class="profile-main">
          <div class="profile-heading">
            <div class="profile-name">{{ headerProfile.name }}</div>
          </div>
          <div class="profile-meta" v-if="headerProfile.meta">{{ headerProfile.meta }}</div>
          <div class="profile-date" v-if="role === 'student'">入营时间：{{ headerProfile.joinDate }}</div>
        </div>
        <camp-role-switcher
          v-if="roleOptions.length > 1"
          :roles="roleOptions"
          :active-role-code="viewRole"
          @change="changeRole"
        ></camp-role-switcher>
      </div>
    </div>
    <!-- 报错提示 -->
    <div class="camp-empty" v-if="loadError">{{ loadError }}</div>
    <!-- 学员首页 -->
    <camp-student-home v-if="role === 'student'" :key="campConfigId + ':' + viewRole" :camp-config-id="campConfigId" :view-role="viewRole" @profile-change="setStudentProfile"></camp-student-home>
    <!-- 主任首页 -->
    <camp-director-home v-if="role === 'director'" :key="campConfigId + ':' + viewRole" :camp-config-id="campConfigId" :view-role="viewRole"></camp-director-home>
    <!-- PD 首页 -->
    <camp-pd-home v-if="role === 'pd'" :key="campConfigId + ':' + viewRole" :camp-config-id="campConfigId" :view-role="viewRole"></camp-pd-home>
    <!-- 原中心、PE 首页 -->
    <camp-origin-home v-if="role === 'origin' || role === 'pe'" :key="campConfigId + ':' + viewRole" :camp-config-id="campConfigId" :view-role="viewRole"></camp-origin-home>
  </div>
</template>

<script>
import CampStudentHome from './components/CampStudentHome.vue'
import CampDirectorHome from './components/CampDirectorHome.vue'
import CampPdHome from './components/CampPdHome.vue'
import CampOriginHome from './components/CampOriginHome.vue'
import CampRoleSwitcher from './components/CampRoleSwitcher.vue'
import withRouteTracking from '@/utils/withRouteTracking'
import { getResearchCampContext } from '@/api/researchCamp'

const CampMine = {
  name: 'CampMine',
  components: {
    CampStudentHome,
    CampDirectorHome,
    CampPdHome,
    CampOriginHome,
    CampRoleSwitcher
  },
  data () {
    return {
      role: '',
      viewRole: '',
      campConfigId: null,
      currentUser: {},
      studentProfile: { name: '', meta: '', joinDate: '' },
      availableRoles: [],
      contextRequestId: 0,
      loading: true,
      loadError: ''
    }
  },
  created () {
    this.loadContext()
  },
  computed: {
    // 同一角色只保留当前营的那一条
    roleOptions () {
      const options = new Map()
      this.availableRoles.forEach(item => {
        if (!options.has(item.roleCode)) options.set(item.roleCode, item)
        if (String(item.campConfigId) === String(this.campConfigId)) options.set(item.roleCode, item)
      })
      return Array.from(options.values())
    },
    // PD 进入单个中心
    fromPd () {
      return this.$route.query.from === 'pd'
    },
    // 学员资料来自学员页，其余角色统一使用登录用户资料
    headerProfile () {
      if (this.role === 'student') return this.studentProfile
      const user = this.currentUser || {}
      const useEmail = this.role === 'pd' || this.role === 'pe'
      return {
        name: user.displayName || '',
        meta: useEmail
          ? (user.email || '')
          : [user.hospitalName, user.departmentL1, user.positionTitle].filter(Boolean).join(' '),
        joinDate: ''
      }
    }
  },
  watch: {
    '$route.query': {
      handler () {
        this.loadContext()
      },
      deep: true
    }
  },
  methods: {
    // 按账号角色决定进入哪个研修营主页
    async loadContext () {
      const requestId = ++this.contextRequestId
      this.loading = true
      this.loadError = ''
      this.role = ''
      this.viewRole = ''
      this.campConfigId = null
      this.currentUser = {}
      this.studentProfile = { name: '', meta: '', joinDate: '' }
      this.availableRoles = []
      try {
        const response = await getResearchCampContext()
        if (requestId !== this.contextRequestId) return
        const context = response && response.data ? response.data : {}
        const contextUser = context.user || {}
        const userInfo = this.$store.getters.userInfo || {}
        this.currentUser = Object.assign({}, contextUser, {
          email: contextUser.email || userInfo.email || '',
          hospitalName: contextUser.hospitalName || userInfo.hospitalName || '',
          departmentL1: contextUser.departmentL1 || userInfo.departmentL1 || '',
          departmentL2: contextUser.departmentL2 || userInfo.departmentL2 || '',
          major: contextUser.major || userInfo.major || '',
          positionTitle: contextUser.positionTitle || userInfo.positionTitle || ''
        })
        const roles = Array.isArray(context.availableRoles) ? context.availableRoles : []
        this.availableRoles = roles
        const requestedCampId = this.$route.query.researchCampId
        const campConfigId = requestedCampId || context.defaultCampConfigId
        if (context.requiresCampSelection || !campConfigId) {
          this.loadError = '当前账号没有已关联的研修营'
          return
        }
        const requestedRole = this.$route.query.viewRole || (this.$route.query.from === 'pd' ? 'PD' : '')
        const campRoles = roles.filter(item => String(item.campConfigId) === String(campConfigId))
        const role = campRoles.find(item => item.roleCode === requestedRole) ||
          (!requestedRole ? campRoles[0] : null)
        if (!role) {
          this.loadError = '当前账号无权访问该研修营'
          return
        }
        const roleNames = {
          LEARNER: 'student',
          CAMP_DIRECTOR: 'director',
          PD: 'pd',
          ORIGINAL_CENTER_CAMP_DIRECTOR: 'origin',
          ORIGINAL_CENTER_FPE: 'pe'
        }
        const pdSingleCampView = this.$route.query.from === 'pd' && role.roleCode === 'PD'
        this.role = pdSingleCampView ? 'director' : (roleNames[role.roleCode] || '')
        this.viewRole = role.roleCode
        this.campConfigId = campConfigId
        if (!this.role) this.loadError = '当前账号的研修营角色暂不支持'
      } catch (error) {
        if (requestId === this.contextRequestId) this.loadError = '研修营角色加载失败'
      } finally {
        if (requestId === this.contextRequestId) this.loading = false
      }
    },
    // 学员页加载完资料后回填头部
    setStudentProfile (profile) {
      this.studentProfile = profile || { name: '', meta: '', joinDate: '' }
    },
    // 切换角色后刷新当前营
    changeRole (role) {
      const authorizedRole = this.availableRoles.find(item =>
        item.roleCode === role.roleCode && String(item.campConfigId) === String(role.campConfigId)
      )
      if (!authorizedRole) return
      if (role.roleCode === this.viewRole && String(role.campConfigId) === String(this.campConfigId)) return

      const query = {
        researchCampId: String(authorizedRole.campConfigId),
        viewRole: authorizedRole.roleCode
      }
      if (authorizedRole.roleCode === 'PD' && this.$route.query.from === 'pd') query.from = 'pd'
      this.$router.replace({ path: this.$route.path, query })
    }
  }
}

export default withRouteTracking(CampMine)
</script>

<style lang="less" scoped>
@import './camp.less';

.camp-mine-shell {
  .camp-page();
  background-image: url('../../assets/images/researchCamp/banner.png');
  background-repeat: no-repeat;
  background-position: center top;
  background-size: 100% auto;

  .mine-head {
    padding-top: 170px;
  }

  .mine-profile {
    .camp-white-card();
    display: flex;
    align-items: flex-start;
    min-height: 78px;
    padding: 16px;
    gap: 10px;
    background: #fff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  }

  .profile-avatar {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    object-fit: cover;
    overflow: hidden;
    flex-shrink: 0;
  }

  .profile-main {
    flex: 1;
    min-width: 0;
  }

  .profile-heading {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .profile-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    line-height: 20px;
    color: @camp-text;
    margin-top: 4px;
  }

  .profile-meta,
  .profile-date {
    font-size: 12px;
    line-height: 20px;
    color: @camp-sub;
    word-break: break-all;
  }

  .profile-date {
    font-size: 11px;
  }

  .camp-role-switcher {
    margin-left: auto;
  }

  .camp-empty {
    .camp-white-card();
    margin-top: 16px;
    padding: 28px 16px;
    text-align: center;
    font-size: 14px;
    line-height: 20px;
    color: @camp-red;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/researchCamp/mine.vue